import { useState, useSyncExternalStore } from 'react'
import { skipToken, useQuery } from '@tanstack/react-query'

import { fetchIdentity, getSessionEpoch, subscribe, useAuth } from './auth'
import { IAM_PERMISSIONS, can, type PermissionKey } from './permissions'
import { useSelectedEntityId } from './use-selected-entity'

type Identity = Awaited<ReturnType<typeof fetchIdentity>>

export function usePermissions(): {
  can: (key: PermissionKey) => boolean
  ready: boolean
} {
  const { user } = useAuth()
  const selectedEntityId = useSelectedEntityId()
  const scoped =
    user?.permissions !== undefined && user.entityId === selectedEntityId
  return {
    ready: scoped,
    can: (key) => scoped && can(user?.permissions, key),
  }
}

export function usePermissionsAt(entityId?: string | null): ReturnType<typeof usePermissions> & {
  error: Error | null
  retrying: boolean
  refetch: () => Promise<void>
} {
  const { user, tokenContext } = useAuth()
  const selectedEntityId = useSelectedEntityId()
  const target = entityId === undefined ? selectedEntityId : entityId
  const epoch = useSyncExternalStore(subscribe, getSessionEpoch, getSessionEpoch)
  const scoped = target === selectedEntityId && user?.entityId === target && user?.permissions !== undefined
  const fetchOwner = target !== null && user !== null && target !== selectedEntityId
  const owner = useQuery({
    queryKey: [
      'entity-permissions', epoch,
      tokenContext.principal,
      tokenContext.generation,
      user?.userId,
      user?.impersonation?.actorUserId,
      target,
    ],
    queryFn: fetchOwner
      ? async () => {
          const identity = await fetchIdentity(target)
          if (!describesOwner(identity, user, target))
            throw new Error('The permission lookup answered for a different identity.')
          return identity
        }
      : skipToken,
    placeholderData: (previous, query) =>
      tokenContext.principal !== undefined && query?.queryKey[1] === epoch && query.queryKey[2] === tokenContext.principal
        ? previous : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const [lastError, setLastError] = useState<Error | null>(null)
  if (owner.error !== null && owner.error !== lastError) setLastError(owner.error)
  // A failed refresh keeps the loaded permissions; the server still authorizes every change.
  const loaded = fetchOwner && describesOwner(owner.data, user, target)
  const ready = scoped || loaded
  const retrying = fetchOwner && !loaded && owner.isFetching && owner.errorUpdateCount > 0
  const failed = fetchOwner && !loaded && (owner.isError || retrying)
  const permissions = scoped ? user.permissions : owner.data?.permissions
  return {
    ready,
    can: (key) => ready && can(permissions, key),
    error: failed ? owner.error ?? lastError : null,
    retrying,
    refetch: async () => {
      if (fetchOwner) await owner.refetch()
    },
  }
}

function describesOwner(identity: Identity | undefined, user: Identity, target: string) {
  return identity?.userId === user.userId && identity.entityId === target &&
    identity.impersonation?.actorUserId === user.impersonation?.actorUserId &&
    identity.permissions !== undefined
}

export function useCanAdministerIam(): boolean {
  const { can: allows } = usePermissions()
  return allows(IAM_PERMISSIONS.manage)
}
